import { SignalDriveTypeSchema } from '@cadstrom/sigma-engine-shared';
import ButtonBase from '@mui/material/ButtonBase';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import clsx from 'clsx';
import { useState } from 'react';

import { useParsedDirectiveRows } from './use-parsed-directive-rows.js';

import { DirectiveApplyBar } from './directive-apply-bar.js';

interface DriveModesRow {
	signalName: string;
	type: string;
	parameters?: {
		outputHighVoltage?: number | null;
		outputLowVoltage?: number | null;
		inputHighVoltage?: number | null;
		inputLowVoltage?: number | null;
		outputImpedance?: number | null;
	};
}

export interface DriveModesTableMarkdownItemProps {
	/** Raw JSON string encoding an array of {@link DriveModesRow} entries. */
	jsonData?: string;
}

type ParameterKey = keyof NonNullable<DriveModesRow['parameters']>;

const editInputClass =
	'w-full min-w-10 bg-transparent border-b border-gray-a6 focus:outline-none focus:border-blue-9 px-1 py-0.5';

const driveTypes: string[] = Object.values(SignalDriveTypeSchema.enum);

function parseDriveModes(jsonData: string): DriveModesRow[] | null {
	try {
		const parsed: unknown = JSON.parse(jsonData);
		if (!Array.isArray(parsed)) {
			return null;
		}
		const rows = parsed.filter(
			(item): item is DriveModesRow =>
				typeof item === 'object' &&
				item !== null &&
				typeof (item as Record<string, unknown>)['signalName'] === 'string' &&
				typeof (item as Record<string, unknown>)['type'] === 'string',
		);
		return rows.length > 0 ? rows : null;
	} catch {
		return null;
	}
}

/**
 * Markdown directive component that renders a compact drive-modes table inside a workbench message.
 *
 * Parses `jsonData` as `Array<DriveModesRow>` and displays one row per signal with columns for
 * drive type and the four standard voltage thresholds plus output impedance. Rows are editable
 * inline before applying. Renders `null` when `jsonData` is absent or not valid JSON.
 *
 * @param jsonData - Raw JSON string containing the drive-modes array.
 */
export function DriveModesTableMarkdownItem({ jsonData }: DriveModesTableMarkdownItemProps) {
	const [rows, setRows] = useParsedDirectiveRows(jsonData, parseDriveModes);
	const [isEditing, setIsEditing] = useState(false);

	if (!rows) {
		return null;
	}

	function updateRow(index: number, patch: Partial<DriveModesRow>) {
		setRows((prev) => (prev ? prev.map((r, i) => (i === index ? { ...r, ...patch } : r)) : null));
	}

	function updateRowParam(index: number, key: ParameterKey, value: number | null) {
		setRows((prev) =>
			prev ? prev.map((r, i) => (i === index ? { ...r, parameters: { ...r.parameters, [key]: value } } : r)) : null,
		);
	}

	function parseNumericInput(value: string): number | null {
		if (value === '') {
			return null;
		}
		const n = Number(value);
		return Number.isFinite(n) ? n : null;
	}

	return (
		<>
			<div className='my-1 overflow-x-auto'>
				<table className='w-full border-collapse text-sm'>
					<thead>
						<tr className='border-b border-gray-a4'>
							{(['Signal', 'Drive Type', 'VOH', 'VOL', 'VIH', 'VIL', 'Impedance'] as const).map((header) => (
								<th
									key={header}
									className={clsx(
										'px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11',
										header === 'Signal' ? 'min-w-24' : 'min-w-14',
									)}
								>
									{header}
								</th>
							))}
							<th className='px-2 py-1 text-right'>
								<ButtonBase
									className='text-xs font-normal tracking-normal text-gray-11 normal-case hover:text-gray-12'
									onClick={() => setIsEditing((v) => !v)}
								>
									{isEditing ? 'Done' : 'Edit'}
								</ButtonBase>
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<tr
								key={index}
								className={clsx('border-b border-gray-a3', index % 2 === 0 ? 'bg-transparent' : 'bg-gray-a2')}
							>
								<td className='px-2 py-1 font-mono whitespace-nowrap text-gray-12'>
									{isEditing ?
										<input
											type='text'
											value={row.signalName}
											onChange={(e) => updateRow(index, { signalName: e.target.value })}
											className={editInputClass}
										/>
									:	row.signalName}
								</td>
								<td className='px-2 py-1 whitespace-nowrap text-gray-12'>
									{isEditing ?
										<Select
											value={row.type}
											onChange={(e) => updateRow(index, { type: e.target.value })}
											variant='standard'
											size='small'
											className='w-full min-w-10'
										>
											{/* Keeping an unrecognized type selectable shows what arrived, rather than
											    letting the cell read as the first valid option. */}
											{!driveTypes.includes(row.type) && (
												<MenuItem value={row.type}>
													<span>{row.type} (not a drive type)</span>
												</MenuItem>
											)}
											{driveTypes.map((driveType) => (
												<MenuItem key={driveType} value={driveType}>
													{driveType}
												</MenuItem>
											))}
										</Select>
									:	row.type}
								</td>
								<td className='px-2 py-1 text-gray-12'>
									{isEditing ?
										<input
											type='number'
											value={row.parameters?.outputHighVoltage ?? ''}
											onChange={(e) => updateRowParam(index, 'outputHighVoltage', parseNumericInput(e.target.value))}
											className={editInputClass}
										/>
									:	(row.parameters?.outputHighVoltage ?? '—')}
								</td>
								<td className='px-2 py-1 text-gray-12'>
									{isEditing ?
										<input
											type='number'
											value={row.parameters?.outputLowVoltage ?? ''}
											onChange={(e) => updateRowParam(index, 'outputLowVoltage', parseNumericInput(e.target.value))}
											className={editInputClass}
										/>
									:	(row.parameters?.outputLowVoltage ?? '—')}
								</td>
								<td className='px-2 py-1 text-gray-12'>
									{isEditing ?
										<input
											type='number'
											value={row.parameters?.inputHighVoltage ?? ''}
											onChange={(e) => updateRowParam(index, 'inputHighVoltage', parseNumericInput(e.target.value))}
											className={editInputClass}
										/>
									:	(row.parameters?.inputHighVoltage ?? '—')}
								</td>
								<td className='px-2 py-1 text-gray-12'>
									{isEditing ?
										<input
											type='number'
											value={row.parameters?.inputLowVoltage ?? ''}
											onChange={(e) => updateRowParam(index, 'inputLowVoltage', parseNumericInput(e.target.value))}
											className={editInputClass}
										/>
									:	(row.parameters?.inputLowVoltage ?? '—')}
								</td>
								<td className='px-2 py-1 text-gray-12'>
									{isEditing ?
										<input
											type='number'
											value={row.parameters?.outputImpedance ?? ''}
											onChange={(e) => updateRowParam(index, 'outputImpedance', parseNumericInput(e.target.value))}
											className={editInputClass}
										/>
									:	(row.parameters?.outputImpedance ?? '—')}
								</td>
								<td />
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<DirectiveApplyBar data={rows} configId='signal-drive-modes' />
		</>
	);
}
